/**
 * Mistura de vários microfones numa track só (spec 016).
 *
 * Quem consome (Ao vivo, escuta, gravação) recebe um `MediaStream` comum e
 * não sabe que por trás há vários aparelhos.
 *
 * Por que sem AudioContext: o "Jarvis" abre o Ao vivo sem clique, e sem
 * gesto na página o Chrome cria o AudioContext SUSPENSO (ver
 * `liveSession.ts`) — a mistura ficaria muda. Então cada microfone é lido
 * direto da track (`MediaStreamTrackProcessor`) e a soma sai por um
 * `MediaStreamTrackGenerator`. Os dois são do Chrome (94+); sem eles,
 * `abrirMistura` devolve null e quem chamou cai no microfone padrão.
 *
 * Relógio: o primeiro aparelho que entregar som manda o ritmo; cada um dos
 * outros tem uma fila (os relógios dos aparelhos escorregam devagar). Fila
 * vazia = aquele microfone fica em silêncio até juntar 50 ms; fila acima de
 * 200 ms = descarta o mais velho. Se o aparelho do relógio sumir (bateria,
 * cabo), o próximo que entregar som assume.
 *
 * Processamento: aparelho inteiro abre com o eco e o ruído do Chrome
 * ligados (como o Ao vivo sempre abriu) e SEM ganho automático — o ganho
 * automático sobe o chiado do microfone distante no silêncio e confunde o
 * corte; o volume de cada um é manual. Aparelho dividido (um transmissor por
 * canal) abre sem processamento nenhum, porque o processamento do Chrome
 * junta os canais num só.
 */

import { DetectorCanais, Fifo, Gate, MIX_RATE, Resampler, dbParaGanho, limitar, rms, somar } from './dsp';
import { DetectorFalante, type Falante } from './falante';
import type { Quem } from './store';

/** Quadro do `MediaStreamTrackProcessor` (o `AudioData` do WebCodecs). */
interface AudioFrame {
  numberOfFrames: number;
  numberOfChannels: number;
  sampleRate: number;
  copyTo(dest: Float32Array, opts: { planeIndex: number; format?: string }): void;
  close(): void;
}
type TrackProcessorCtor = new (init: { track: MediaStreamTrack }) => {
  readable: { getReader(): ReadableStreamDefaultReader<AudioFrame> };
};
type GeneratedTrack = MediaStreamTrack & { writable: WritableStream<unknown> };
type TrackGeneratorCtor = new (init: { kind: 'audio' }) => GeneratedTrack;
type AudioDataCtor = new (init: {
  format: 'f32-planar';
  sampleRate: number;
  numberOfFrames: number;
  numberOfChannels: number;
  timestamp: number;
  data: Float32Array;
}) => unknown;

interface Apis {
  Processor: TrackProcessorCtor;
  Generator: TrackGeneratorCtor;
  AudioData: AudioDataCtor;
}

function apis(): Apis | null {
  const g = globalThis as {
    MediaStreamTrackProcessor?: TrackProcessorCtor;
    MediaStreamTrackGenerator?: TrackGeneratorCtor;
    AudioData?: AudioDataCtor;
  };
  if (!g.MediaStreamTrackProcessor || !g.MediaStreamTrackGenerator || !g.AudioData) return null;
  return { Processor: g.MediaStreamTrackProcessor, Generator: g.MediaStreamTrackGenerator, AudioData: g.AudioData };
}

/** Este navegador consegue misturar (Chrome)? */
export function misturaSuportada(): boolean {
  return apis() !== null;
}

export interface FonteMistura {
  /** Chave da preferência (`store.chaveMic`). */
  chave: string;
  deviceId: string;
  canal?: 'L' | 'R';
  volumeDb: number;
  corte: number;
  /** Quem este microfone ouve (fase 2: a lapela "médico" contra o resto). */
  quem?: Quem;
}

export interface OpcoesMistura {
  /** Lapela acima da sala por isto (dB) = médico falando. */
  limiarFalanteDb?: number;
}

export interface NivelFonte {
  /** RMS depois do volume, antes do corte (0–1). */
  nivel: number;
  /** Passou do corte (está somando na mistura). */
  aberto: boolean;
  /** O aparelho ainda entrega som. */
  vivo: boolean;
}

export interface Mistura {
  stream: MediaStream;
  /** Fontes que abriram (as que falharam ficaram de fora). */
  chaves: string[];
  niveis(): Record<string, NivelFonte>;
  /** Dá pra saber quem fala (um "médico" e mais alguém abriram)? */
  separaFalante: boolean;
  /** Quem fala agora: null = silêncio ou sem como saber. */
  falante(): Falante | null;
  parar(): void;
}

const FILA_MAX = MIX_RATE / 5;
const FILA_ALVO = MIX_RATE / 20;

interface EstadoFonte {
  f: FonteMistura;
  fila: Fifo;
  gate: Gate;
  ganho: number;
  res: Resampler | null;
  pronta: boolean;
  nivel: number;
  aberto: boolean;
}

interface Aparelho {
  track: MediaStreamTrack;
  fontes: EstadoFonte[];
  vivo: boolean;
}

/** Restrições de abertura de um aparelho (ver o porquê no topo do arquivo). */
export function restricoesAparelho(deviceId: string, dividido: boolean): MediaTrackConstraints {
  return dividido
    ? { deviceId: { exact: deviceId }, channelCount: { ideal: 2 }, echoCancellation: false, noiseSuppression: false, autoGainControl: false }
    : { deviceId: { exact: deviceId }, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: false };
}

/** Copia os canais do quadro (no máximo 2) como Float32 separados. */
function lerCanais(frame: AudioFrame): Float32Array[] {
  const canais: Float32Array[] = [];
  for (let c = 0; c < Math.min(frame.numberOfChannels, 2); c++) {
    const b = new Float32Array(frame.numberOfFrames);
    frame.copyTo(b, { planeIndex: c, format: 'f32-planar' });
    canais.push(b);
  }
  return canais;
}

function media(a: Float32Array, b: Float32Array): Float32Array {
  const out = new Float32Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = (a[i] + (b[i] ?? 0)) / 2;
  return out;
}

/** O som de UMA fonte dentro do quadro do aparelho. */
function sinalDaFonte(canais: Float32Array[], canal?: 'L' | 'R'): Float32Array {
  if (canal === 'L') return canais[0];
  if (canal === 'R') return canais[1] ?? canais[0];
  return canais.length >= 2 ? media(canais[0], canais[1]) : canais[0];
}

/** Lê os quadros de uma track até ela acabar ou `parado()` virar true. */
function lerTrack(Processor: TrackProcessorCtor, track: MediaStreamTrack, aoQuadro: (f: AudioFrame) => void, parado: () => boolean, fim: () => void): ReadableStreamDefaultReader<AudioFrame> | null {
  let reader: ReadableStreamDefaultReader<AudioFrame>;
  try {
    reader = new Processor({ track }).readable.getReader();
  } catch {
    return null;
  }
  void (async () => {
    for (;;) {
      let r: ReadableStreamReadResult<AudioFrame>;
      try {
        r = await reader.read();
      } catch {
        break;
      }
      if (r.done) break;
      try {
        if (parado()) break;
        aoQuadro(r.value);
      } catch {
        /* quadro ilegível: pula */
      } finally {
        r.value.close();
      }
    }
    fim();
  })();
  return reader;
}

/**
 * Abre os microfones e devolve a mistura. Null se o navegador não sabe
 * misturar ou se nenhum aparelho abriu — quem chamou cai no padrão.
 */
export async function abrirMistura(fontes: readonly FonteMistura[], opcoes: OpcoesMistura = {}): Promise<Mistura | null> {
  const api = apis();
  if (!api || fontes.length === 0) return null;

  const porAparelho = new Map<string, FonteMistura[]>();
  for (const f of fontes) porAparelho.set(f.deviceId, [...(porAparelho.get(f.deviceId) ?? []), f]);

  const abertos = await Promise.all(
    [...porAparelho].map(async ([deviceId, fs]) => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: restricoesAparelho(deviceId, fs.some((f) => f.canal)) });
        const track = s.getAudioTracks()[0];
        return track ? { track, fs } : null;
      } catch {
        return null;
      }
    }),
  );
  const aparelhos: Aparelho[] = abertos
    .filter((a): a is { track: MediaStreamTrack; fs: FonteMistura[] } => a !== null)
    .map(({ track, fs }) => ({
      track,
      vivo: true,
      fontes: fs.map((f) => ({
        f,
        fila: new Fifo(FILA_MAX * 2),
        gate: new Gate(MIX_RATE, f.corte),
        ganho: dbParaGanho(f.volumeDb),
        res: null,
        pronta: false,
        nivel: 0,
        aberto: false,
      })),
    }));
  if (aparelhos.length === 0) return null;

  let gen: GeneratedTrack;
  try {
    gen = new api.Generator({ kind: 'audio' });
  } catch {
    aparelhos.forEach((a) => a.track.stop());
    return null;
  }
  const writer = gen.writable.getWriter();
  let parado = false;
  let relogio: Aparelho | null = null;
  let ts = 0;

  // Fase 2: a lapela do médico contra o mais alto da sala, bloco a bloco.
  const todas = aparelhos.flatMap((a) => a.fontes.map((ef) => ({ a, ef })));
  const lapela = todas.filter(({ ef }) => ef.f.quem === 'medico');
  const separa = lapela.length === 1 && todas.length >= 2;
  const detector = separa ? new DetectorFalante(opcoes.limiarFalanteDb) : null;
  let falanteAgora: Falante | null = null;
  const medirFalante = (n: number) => {
    if (!detector) return;
    const med = lapela[0];
    if (!med.a.vivo) {
      falanteAgora = null;
      return;
    }
    let sala = 0;
    let voz = med.ef.aberto;
    for (const { a, ef } of todas) {
      if (ef === med.ef || !a.vivo) continue;
      sala = Math.max(sala, ef.nivel);
      voz ||= ef.aberto;
    }
    falanteAgora = detector.push(med.ef.nivel, sala, voz, n / MIX_RATE);
  };

  const emitir = (n: number) => {
    const out = new Float32Array(n);
    for (const a of aparelhos) {
      if (!a.vivo) continue;
      for (const ef of a.fontes) {
        let x: Float32Array;
        if (a === relogio) {
          x = ef.fila.pull(n);
        } else {
          if (!ef.pronta && ef.fila.size >= FILA_ALVO + n) ef.pronta = true;
          if (ef.pronta && ef.fila.size < n) ef.pronta = false;
          x = ef.pronta ? ef.fila.pull(n) : new Float32Array(n);
          if (ef.fila.size > FILA_MAX) ef.fila.manterUltimas(FILA_ALVO);
        }
        if (ef.ganho !== 1) for (let i = 0; i < n; i++) x[i] *= ef.ganho;
        ef.nivel = rms(x);
        ef.aberto = ef.gate.process(x);
        somar(out, x, 1);
      }
    }
    medirFalante(n);
    limitar(out);
    const quadro = new api.AudioData({ format: 'f32-planar', sampleRate: MIX_RATE, numberOfFrames: n, numberOfChannels: 1, timestamp: ts, data: out });
    ts += Math.round((n / MIX_RATE) * 1e6);
    writer.write(quadro).catch(() => {});
  };

  const aoQuadro = (a: Aparelho, frame: AudioFrame) => {
    const canais = lerCanais(frame);
    if (canais.length === 0) return;
    let n = 0;
    for (const ef of a.fontes) {
      let x = sinalDaFonte(canais, ef.f.canal);
      if (frame.sampleRate !== MIX_RATE) {
        ef.res ??= new Resampler(frame.sampleRate, MIX_RATE);
        x = ef.res.process(x);
      }
      ef.fila.push(x);
      n = x.length;
    }
    if (!relogio || !relogio.vivo) relogio = a;
    if (relogio === a && n > 0) emitir(n);
  };

  const parar = () => {
    if (parado) return;
    parado = true;
    aparelhos.forEach((a) => a.track.stop());
    writer.close().catch(() => {});
    pararGerada();
  };

  // Quem consome para a track com `stop()`, que não dispara 'ended' nem avisa
  // ninguém: embrulha o `stop` desta instância pra soltar os microfones junto.
  const pararGerada = gen.stop.bind(gen);
  gen.stop = () => {
    pararGerada();
    parar();
  };

  for (const a of aparelhos) {
    const reader = lerTrack(
      api.Processor,
      a.track,
      (f) => aoQuadro(a, f),
      () => parado,
      () => {
        a.vivo = false;
        if (relogio === a) relogio = null;
      },
    );
    if (!reader) {
      a.vivo = false;
      a.track.stop();
    }
  }
  if (!aparelhos.some((a) => a.vivo)) {
    parar();
    return null;
  }

  return {
    stream: new MediaStream([gen]),
    chaves: aparelhos.filter((a) => a.vivo).flatMap((a) => a.fontes.map((ef) => ef.f.chave)),
    niveis: () => {
      const out: Record<string, NivelFonte> = {};
      for (const a of aparelhos) for (const ef of a.fontes) out[ef.f.chave] = { nivel: ef.nivel, aberto: ef.aberto, vivo: a.vivo };
      return out;
    },
    separaFalante: separa,
    falante: () => falanteAgora,
    parar,
  };
}

export interface NivelAparelho {
  /** RMS do aparelho inteiro (o maior dos canais). */
  total: number;
  esq?: number;
  dir?: number;
  /** 2 canais: o mesmo som nos dois, ou sons diferentes (null = ainda ouvindo). */
  canais: 'mono' | 'iguais' | 'diferentes' | null;
}

export interface Monitor {
  niveis(): Record<string, NivelAparelho>;
  parar(): void;
}

/** Nível que cai devagar (~3 dB a cada 100 ms): sílaba curta aparece no medidor. */
const QUEDA = 0.93;

/**
 * Só ouvir (tela de Configurações): abre cada aparelho SEM processamento e
 * com 2 canais se tiver, mede o nível de cada canal e descobre se os canais
 * trazem sons diferentes. Nada sai do computador.
 */
export async function abrirMonitor(deviceIds: readonly string[]): Promise<Monitor | null> {
  const api = apis();
  if (!api) return null;
  let parado = false;
  const estado = new Map<string, { track: MediaStreamTrack; nivel: NivelAparelho; det: DetectorCanais }>();
  await Promise.all(
    deviceIds.map(async (id) => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: restricoesAparelho(id, true) });
        const track = s.getAudioTracks()[0];
        if (!track) return;
        if (parado) {
          track.stop();
          return;
        }
        const e = { track, nivel: { total: 0, canais: null } as NivelAparelho, det: new DetectorCanais() };
        estado.set(id, e);
        lerTrack(
          api.Processor,
          track,
          (frame) => {
            const canais = lerCanais(frame);
            if (canais.length === 0) return;
            const n = e.nivel;
            if (canais.length === 1) {
              n.total = Math.max(rms(canais[0]), n.total * QUEDA);
              n.canais = 'mono';
              return;
            }
            n.esq = Math.max(rms(canais[0]), (n.esq ?? 0) * QUEDA);
            n.dir = Math.max(rms(canais[1]), (n.dir ?? 0) * QUEDA);
            n.total = Math.max(n.esq, n.dir);
            e.det.push(canais[0], canais[1]);
            n.canais = e.det.resultado;
          },
          () => parado,
          () => {},
        );
      } catch {
        /* aparelho ocupado ou sumiu: fica sem medidor */
      }
    }),
  );
  return {
    niveis: () => {
      const out: Record<string, NivelAparelho> = {};
      for (const [id, e] of estado) out[id] = { ...e.nivel };
      return out;
    },
    parar: () => {
      parado = true;
      estado.forEach((e) => e.track.stop());
    },
  };
}
